import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, openApiFile, shareApiPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, FileText, Download, CheckCircle2, Mail, FileBadge2, Eye, Edit2, History, Search, Share2, Loader2, Truck, RotateCcw, Copy, UserCog, FileEdit, MoreHorizontal, Ban, Hash } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { usePagedList, PaginationBar } from "@/components/Pagination";
import { useAuth } from "@/contexts/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import MarcarAnuladaSriDialog from "@/components/MarcarAnuladaSriDialog";

const ESTADO_STYLE = {
  borrador: "bg-gray-100 text-gray-700",
  firmado: "bg-blue-100 text-blue-700",
  enviado: "bg-indigo-100 text-indigo-700",
  autorizado: "bg-[#def5e6] text-[#1f6b42] border-[#9dd4ae]",
  rechazado: "bg-[#f5d6cf] text-[#7a2514] border-[#d99584]",
  anulado: "bg-gray-200 text-gray-500",
};

export default function Invoicing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [config, setConfig] = useState({});
  const [historyOpen, setHistoryOpen] = useState(null);
  const [cartaPicker, setCartaPicker] = useState(null);
  const [reasignarFc, setReasignarFc] = useState(null); // factura a reasignar cliente
  const [anularSriFc, setAnularSriFc] = useState(null); // factura a marcar como anulada en SRI
  const [searchQ, setSearchQ] = useState("");
  const [filterEstado, setFilterEstado] = useState("todos");
  const [filterCliente, setFilterCliente] = useState("");
  const [filterFechaDesde, setFilterFechaDesde] = useState("");
  const [filterFechaHasta, setFilterFechaHasta] = useState("");
  const [guiaSheet, setGuiaSheet] = useState(null); // { factura, payload, cartones, ... }
  const [guiaSaving, setGuiaSaving] = useState(false);

  const load = async () => {
    const [f, c, cf] = await Promise.all([
      api.get("/invoicing/facturas"),
      api.get("/masters/clientes"),
      api.get("/invoicing/config"),
    ]);
    setItems(f.data);
    setClientes(c.data.filter(x => x.estado === "aprobado"));
    setConfig(cf.data);
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); }, []);

  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("f");
  useEffect(() => {
    if (!highlightId || items.length === 0) return;
    const el = document.querySelector(`[data-invoice-row="${highlightId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-[#205c45]", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-[#205c45]", "ring-offset-2"), 3000);
    }
  }, [highlightId, items]);

  const editarFactura = (it) => {
    if (!["borrador", "firmado", "rechazado"].includes(it.estado_sri)) {
      return toast.error(`No se puede editar una factura en estado '${it.estado_sri}'`);
    }
    if (it.estado_sri === "firmado" && !window.confirm("Editar descartará la firma electrónica actual y la factura volverá a estado 'borrador'. ¿Continuar?")) {
      return;
    }
    navigate(`/facturacion/${it.id}/editar`);
  };

  // ITER148 (Feb 2026): soft-edit de campos locales (no toca el XML SRI)
  const [softEditDialog, setSoftEditDialog] = useState(null); // {factura, form}
  const abrirSoftEdit = (it) => {
    const infoActual = {};
    (it.info_adicional || []).forEach(x => { if (x?.nombre) infoActual[x.nombre] = x.valor; });
    setSoftEditDialog({
      factura: it,
      form: {
        observaciones: it.observaciones || "",
        dias_credito: it.dias_credito ?? 0,
        fecha_vencimiento: (it.fecha_vencimiento || "").slice(0, 10),
        comentarios_internos: it.comentarios_internos || "",
        direccion_entrega: it.direccion_entrega || "",
        info_adicional_text: Object.entries(infoActual).map(([k, v]) => `${k}|${v}`).join("\n"),
      },
    });
  };
  const guardarSoftEdit = async () => {
    if (!softEditDialog) return;
    const { factura, form } = softEditDialog;
    const info_adicional = {};
    (form.info_adicional_text || "").split("\n").forEach(line => {
      const [k, ...rest] = line.split("|");
      const key = (k || "").trim();
      const val = (rest.join("|") || "").trim();
      if (key && val) info_adicional[key] = val;
    });
    const payload = {
      observaciones: form.observaciones,
      comentarios_internos: form.comentarios_internos,
      direccion_entrega: form.direccion_entrega,
      info_adicional,
    };
    // dias_credito o fecha_vencimiento (solo uno toma precedencia)
    if (form.fecha_vencimiento) payload.fecha_vencimiento = form.fecha_vencimiento;
    else if (form.dias_credito != null && form.dias_credito !== "") payload.dias_credito = Number(form.dias_credito);
    try {
      const { data } = await api.patch(`/invoicing/facturas/${factura.id}/campos-locales`, payload);
      toast.success(`Campos locales actualizados (${(data.campos_actualizados || []).length})`);
      setSoftEditDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [accionInFlight, setAccionInFlight] = useState({}); // {[factura_id]: "firmar"|"enviar"|"autorizar"|"firmar+enviar+autorizar"}
  const [devolucionDialog, setDevolucionDialog] = useState(null); // { factura, lineas:[{sel,cant_max,cant}], motivo, reponer }

  const iniciarDevolucion = (it) => {
    if (it.estado_sri !== "autorizado") {
      toast.error("Solo se pueden devolver facturas autorizadas por el SRI");
      return;
    }
    setDevolucionDialog({
      factura: it,
      lineas: (it.lineas || []).map((ln, i) => ({
        idx: i,
        producto_nombre: ln.producto_nombre,
        cant_max: Number(ln.cantidad || 0),
        cant: Number(ln.cantidad || 0),
        sel: true,
        precio_unitario: Number(ln.precio_unitario || 0),
        subtotal: Number(ln.subtotal || 0),
      })),
      motivo: "Devolución de mercadería",
      reponer: true,
    });
  };

  const confirmarDevolucion = async () => {
    const d = devolucionDialog;
    if (!d || !d.factura) return;
    if (!d.motivo.trim()) { toast.error("Indique el motivo de la devolución"); return; }
    const lineasSel = d.lineas
      .filter(l => l.sel && Number(l.cant) > 0)
      .map(l => ({ linea_index: l.idx, cantidad: Number(l.cant) }));
    if (lineasSel.length === 0) { toast.error("Seleccione al menos una línea con cantidad > 0"); return; }
    try {
      const { data } = await api.post(`/invoicing/facturas/${d.factura.id}/devolucion`, {
        motivo: d.motivo,
        lineas: lineasSel,
        reponer_stock: !!d.reponer,
      });
      toast.success(
        `NC ${data.nota_credito_numero} creada por $${data.total_devuelto.toFixed(2)}. Vaya a Notas de Crédito para firmar y enviar al SRI.`,
        { duration: 8000 }
      );
      setDevolucionDialog(null);
      load().catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  const accionSRI = async (it, accion) => {
    if (accionInFlight[it.id]) return;
    setAccionInFlight(s => ({ ...s, [it.id]: accion }));
    try {
      const { data } = await api.post(`/invoicing/facturas/${it.id}/${accion}`);
      const mensajes = data?.resultado_recepcion?.mensajes || data?.resultado_autorizacion?.mensajes || [];
      const estado = data?.resultado_recepcion?.estado || data?.resultado_autorizacion?.estado;
      if (estado === "EN_PROCESAMIENTO") {
        toast.message("El SRI aún procesa el comprobante. Consulte de nuevo en unos segundos — no fue rechazado.", { duration: 9000 });
      } else if (estado && mensajes.length > 0) {
        toast.error(`${estado}: ${mensajes[0]?.mensaje || ""} - ${mensajes[0]?.informacionAdicional || ""}`, { duration: 8000 });
      } else if (accion === "firmar") {
        toast.success("XML firmado con el certificado .p12");
      } else if (accion === "enviar") {
        toast.success(`Recepción SRI: ${estado || "OK"}`);
      } else if (accion === "autorizar") {
        if (estado === "AUTORIZADO") toast.success(`AUTORIZADO por el SRI · ${data.resultado_autorizacion?.numero_autorizacion?.slice(0,20)}...`);
        else toast.error(`Estado SRI: ${estado}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAccionInFlight(s => { const n = { ...s }; delete n[it.id]; return n; }); }
  };

  // Acción combinada: ejecuta Firmar (si borrador/rechazada) → Enviar (si firmada) → Autorizar
  const procesarSRI = async (it) => {
    if (accionInFlight[it.id]) return;
    const id = it.id;
    setAccionInFlight(s => ({ ...s, [id]: "firmar+enviar+autorizar" }));
    const toastId = toast.loading("Procesando con el SRI: firmando…", { duration: Infinity });
    try {
      let estadoActual = it.estado_sri;
      // 1) Firmar
      if (estadoActual === "borrador" || estadoActual === "rechazado") {
        await api.post(`/invoicing/facturas/${id}/firmar`);
        toast.loading("Firmado ✓ · Enviando al SRI…", { id: toastId });
        estadoActual = "firmado";
      }
      // 2) Enviar (recepción)
      if (estadoActual === "firmado") {
        const { data: rEnv } = await api.post(`/invoicing/facturas/${id}/enviar`);
        const msgs = rEnv?.resultado_recepcion?.mensajes || [];
        const est = rEnv?.resultado_recepcion?.estado;
        // CLAVE ACCESO REGISTRADA (id 70) NO es rechazo, ya está en el SRI.
        const claveYaRegistrada = msgs.some(m => (String(m.identificador) === "70") || ((m.mensaje || "").toUpperCase().includes("CLAVE") && (m.mensaje || "").toUpperCase().includes("REGISTRADA")));
        const secuencialRegistrado = msgs.some(m => (m.mensaje || "").toUpperCase().includes("SECUENCIAL") && (m.mensaje || "").toUpperCase().includes("REGISTRADO"));
        if (rEnv?.recuperado === "autorizado_con_clave_anterior") {
          toast.success("Esta factura YA estaba AUTORIZADA en el SRI con su clave anterior. Se restauró su autorización.", { id: toastId, duration: 10000 });
          load();
          return;
        }
        if (secuencialRegistrado) {
          toast.error(
            `El SRI ya tiene registrado el número ${it.numero}. Use el menú “…” → “Renumerar” para tomar el siguiente número y reenviar.`,
            { id: toastId, duration: 12000 }
          );
          load();
          return;
        }
        if (est === "ERROR") {
          toast.error(`El servicio del SRI no respondió: ${msgs[0]?.mensaje || "error de comunicación"}. La factura conserva su número y clave — reintente en 1-2 minutos.`, { id: toastId, duration: 12000 });
          load();
          return;
        }
        if (est && est !== "RECIBIDA" && !claveYaRegistrada && msgs.length > 0) {
          toast.error(`Recepción ${est}: ${msgs[0]?.mensaje || ""}`, { id: toastId, duration: 8000 });
          load();
          return;
        }
        if (claveYaRegistrada) {
          toast.loading("Ya estaba en SRI · consultando autorización…", { id: toastId });
        } else {
          toast.loading("Enviado ✓ · Solicitando autorización…", { id: toastId });
        }
        estadoActual = "enviado";
      }
      // 3) Autorizar
      if (estadoActual === "enviado") {
        const { data: rAut } = await api.post(`/invoicing/facturas/${id}/autorizar`);
        const msgs = rAut?.resultado_autorizacion?.mensajes || [];
        const est = rAut?.resultado_autorizacion?.estado;
        if (est === "AUTORIZADO") {
          toast.success(`✅ AUTORIZADO · ${rAut.resultado_autorizacion?.numero_autorizacion?.slice(0,25)}…`, { id: toastId, duration: 6000 });
        } else if (est === "EN_PROCESAMIENTO") {
          toast.message("El SRI aún está procesando el comprobante. Vuelva a presionar “Reintentar” en unos segundos — la factura NO fue rechazada.", { id: toastId, duration: 10000 });
        } else {
          toast.error(`${est}: ${msgs[0]?.mensaje || "Sin autorización"}`, { id: toastId, duration: 8000 });
        }
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e), { id: toastId, duration: 8000 });
    } finally {
      setAccionInFlight(s => { const n = { ...s }; delete n[id]; return n; });
    }
  };

  const _openPdfBlob = (path, filename) => openApiFile(path, { filename }).catch((e) => toast.error(formatApiError(e)));

  // ITER220: nombre de archivo con el número del documento y el cliente
  const _nombreArchivo = (tipo, it, ext = "xml") => {
    const cliente = (it.cliente_nombre || it.cliente?.razon_social || "").replace(/[\\/:*?"<>|]+/g, " ").trim();
    return `${tipo} ${it.numero || ""}${cliente ? ` - ${cliente}` : ""}.${ext}`.replace(/\s{2,}/g, " ");
  };
  const _downloadXml = (path, filename) =>
    openApiFile(path, { filename, mime: "application/xml", downloadOnly: true }).catch((e) => toast.error(formatApiError(e)));
  const descargarXML = (it) => _downloadXml(`/invoicing/facturas/${it.id}/xml`, _nombreArchivo("FACTURA", it));
  const descargarXMLFirmado = (it) => _downloadXml(`/invoicing/facturas/${it.id}/xml-firmado`, _nombreArchivo("FACTURA FIRMADA", it));
  const descargarRIDE = (it) => _openPdfBlob(`/invoicing/facturas/${it.id}/ride`, `RIDE-${it.numero}.pdf`);
  const previewRIDE = (it) => _openPdfBlob(`/invoicing/facturas/${it.id}/ride-preview`, `RIDE-PREVIEW-${it.numero}.pdf`);

  // ITER227: elegir a qué correos del cliente se envía la factura
  const [emailDialog, setEmailDialog] = useState(null); // {factura, correos:[{email,etiqueta,sel}]}
  const enviarEmail = async (it) => {
    try {
      const { data: cli } = await api.get(`/masters/clientes/${it.cliente_id}`);
      const correos = (cli?.correos || []).map(c => ({ ...c, sel: true }));
      if (correos.length === 0) return toast.error("El cliente no tiene correos registrados. Edítelo en Clientes.");
      if (correos.length === 1) return confirmarEnvioEmail(it, [correos[0].email]);
      setEmailDialog({ factura: it, correos });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirmarEnvioEmail = async (factura, emails) => {
    try {
      const { data } = await api.post(`/invoicing/facturas/${factura.id}/enviar-email`, { emails });
      toast.success(`Factura enviada a ${data.destino}`);
      setEmailDialog(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const shareRide = async (it) => {
    try {
      const r = await shareApiPdf(`/invoicing/facturas/${it.id}/ride`, {
        filename: `RIDE-${it.numero}.pdf`,
        title: `Factura ${it.numero}`,
        text: `Adjunto la factura electrónica ${it.numero} por $${(it.total || 0).toFixed(2)}.`,
      });
      if (r.fallback === "downloaded") toast.message("RIDE descargado · Adjúntelo manualmente en WhatsApp");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const generarCarta = async (it, tipo, indiceLinea = 0) => {
    try {
      const { data } = await api.post(`/letters/desde-factura/${it.id}?tipo=${tipo}&indice_linea=${indiceLinea}`);
      toast.success(`Carta ${tipo === "canje" ? "de canje" : "de garantía"} ${data.numero} generada`);
      openApiFile(`/letters/cartas/${data.id}/pdf`, { filename: `CARTA-${data.numero || data.id.slice(0,8)}.pdf` }).catch((e) => toast.error(formatApiError(e)));
      setCartaPicker(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const iniciarCarta = (it, tipo) => {
    const n = (it.lineas || []).length;
    if (n <= 1) return generarCarta(it, tipo, 0);
    setCartaPicker({ factura: it, tipo });
  };

  // ---- Guía de remisión a partir de una factura ----
  const iniciarGuia = async (it) => {
    try {
      const { data } = await api.get(`/invoicing/facturas/${it.id}/guia-preview`);
      setGuiaSheet({
        factura: it,
        cartones: 1,
        transportista: { ...data.transportista },
        fechas: { ...data.fechas },
        destinatario: { ...data.destinatario },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const crearGuia = async () => {
    if (!guiaSheet) return;
    const g = guiaSheet;
    // Validaciones
    if (!g.transportista.razon_social_transportista?.trim()) return toast.error("Indique la razón social del transportista");
    if (!g.transportista.ruc_transportista?.trim()) return toast.error("Indique la identificación del transportista");
    if (!g.transportista.placa?.trim()) return toast.error("Indique la placa del vehículo");
    if (!g.transportista.dir_partida?.trim()) return toast.error("Indique la dirección de partida");
    const cartones = parseInt(g.cartones, 10);
    if (!cartones || cartones < 1) return toast.error("Indique cuántos cartones (≥ 1)");
    if (!g.destinatario.detalles?.length) return toast.error("La factura no tiene líneas para transportar");

    // Confirmación final
    const ok = window.confirm(
      `¿Crear guía de remisión?\n\n` +
      `Transportista: ${g.transportista.razon_social_transportista}\n` +
      `Placa: ${g.transportista.placa}\n` +
      `Partida: ${g.transportista.dir_partida}\n` +
      `Destinatario: ${g.destinatario.razon_social}\n` +
      `Bultos: ${cartones} cartón(es)\n` +
      `Líneas: ${g.destinatario.detalles.length}\n` +
      `Sustento: Factura ${g.destinatario.num_doc_sustento}`
    );
    if (!ok) return;

    setGuiaSaving(true);
    try {
      const obsCartones = `BULTOS: ${cartones} CARTON${cartones !== 1 ? "ES" : ""}`;
      const payload = {
        fecha: g.fechas.fecha,
        dir_partida: g.transportista.dir_partida,
        razon_social_transportista: g.transportista.razon_social_transportista,
        tipo_identificacion_transportista: g.transportista.tipo_identificacion_transportista || "ruc",
        ruc_transportista: g.transportista.ruc_transportista,
        placa: g.transportista.placa,
        fecha_ini_transporte: g.fechas.fecha_ini_transporte,
        fecha_fin_transporte: g.fechas.fecha_fin_transporte,
        destinatarios: [{
          cliente_id: g.destinatario.cliente_id,
          identificacion: g.destinatario.identificacion,
          razon_social: g.destinatario.razon_social,
          direccion: g.destinatario.direccion,
          email: g.destinatario.email || null,
          telefono: g.destinatario.telefono || null,
          motivo_traslado: `${g.destinatario.motivo_traslado || "VENTA"} · ${obsCartones}`,
          ruta: g.destinatario.ruta || null,
          cod_doc_sustento: g.destinatario.cod_doc_sustento || "01",
          num_doc_sustento: g.destinatario.num_doc_sustento || "",
          num_autorizacion_doc_sustento: g.destinatario.num_autorizacion_doc_sustento || null,
          fecha_emision_doc_sustento: g.destinatario.fecha_emision_doc_sustento,
          detalles: g.destinatario.detalles,
        }],
      };
      const { data: guia } = await api.post("/invoicing/guias-remision", payload);
      toast.success(`Guía ${guia.numero} creada (borrador). Cartones: ${cartones}`);
      setGuiaSheet(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGuiaSaving(false); }
  };

  const renumerar = async (it) => {
    const ok = window.confirm(
      `¿Renumerar la factura ${it.numero}?\n\n` +
      `Se le asignará el SIGUIENTE número disponible, con una nueva clave de acceso, y quedará en BORRADOR ` +
      `para firmar y enviar de nuevo.\n\n` +
      `Úselo cuando el SRI responda "ERROR SECUENCIAL REGISTRADO" (ese número ya fue recibido por el SRI).`
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/invoicing/facturas/${it.id}/renumerar`);
      toast.success(`Factura renumerada: ${data.numero_anterior} → ${data.numero}. Ahora presione "Firmar+SRI".`, { duration: 9000 });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const eliminarDoc = async (it) => {
    if (!["borrador", "rechazado"].includes(it.estado_sri)) {
      return toast.error("Solo se pueden eliminar borradores o documentos rechazados (no firmados ni autorizados).");
    }
    if (!window.confirm(`¿Eliminar definitivamente la factura ${it.numero}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/invoicing/facturas/${it.id}`);
      toast.success(`Factura ${it.numero} eliminada`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredItems = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    return items.filter(it => {
      if (filterEstado !== "todos" && it.estado_sri !== filterEstado) return false;
      if (filterCliente && it.cliente_id !== filterCliente) return false;
      if (filterFechaDesde && (it.fecha || "") < filterFechaDesde) return false;
      if (filterFechaHasta && (it.fecha || "") > filterFechaHasta) return false;
      if (q) {
        const inHeader = (
          (it.numero || "").toLowerCase().includes(q) ||
          (it.cliente_nombre || "").toLowerCase().includes(q) ||
          (it.cliente_nombre_comercial || "").toLowerCase().includes(q) ||
          (it.cliente_razon_social || "").toLowerCase().includes(q) ||
          (it.cliente_ruc || "").toLowerCase().includes(q) ||
          (it.clave_acceso || "").toLowerCase().includes(q)
        );
        const inLines = (it.lineas || []).some(l =>
          (l.producto_nombre || "").toLowerCase().includes(q) ||
          (l.codigo_interno || "").toLowerCase().includes(q) ||
          (l.numero_lote || "").toLowerCase().includes(q) ||
          (l.principio_activo || "").toLowerCase().includes(q)
        );
        if (!inHeader && !inLines) return false;
      }
      return true;
    });
  }, [items, searchQ, filterEstado, filterCliente, filterFechaDesde, filterFechaHasta]);

  const pagedFacturas = usePagedList(filteredItems, 20);

  return (
    <div className="space-y-6" data-testid="invoicing-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Facturación electrónica</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Facturas electrónicas SRI</h1>
          <p className="text-sm text-[#4a5c53] mt-1">Genera XML con estructura oficial SRI. Firma y envío se habilitarán al cargar el certificado .p12.</p>
        </div>
        <Button onClick={() => navigate("/facturacion/nueva")} className="bg-[#205c45]" data-testid="create-invoice-btn"><Plus size={16} className="mr-2" />Nueva factura</Button>
      </div>

      <div className="rounded-md border border-[#f5c477] bg-[#fef7ec] p-3 text-xs text-[#8a5500] space-y-1">
        <div><strong>Empresa:</strong> {config.razon_social} · <strong>RUC:</strong> {config.ruc} · <strong>Establecimiento:</strong> {config.establecimiento}-{config.punto_emision} · <strong>Ambiente:</strong> {config.ambiente === "2" ? "PRODUCCIÓN" : "PRUEBAS"}</div>
        <div><strong>Certificado digital:</strong> {config.cert_configurado ? <span className="text-[#1f6b42]">✓ Configurado y listo para firmar</span> : <span className="text-[#7a2514]">✗ No configurado (añada SRI_CERT_PATH y SRI_CERT_PASSWORD en /app/backend/.env)</span>}</div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white p-3 grid md:grid-cols-12 gap-3 items-end" data-testid="invoice-filters">
        <div className="md:col-span-4">
          <Label className="text-xs">Buscar (número, cliente, RUC, producto, lote, principio activo, clave acceso)</Label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8ba397]" />
            <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Ej: AXAPARA, 1709…, lote A23…" className="pl-8" data-testid="invoice-search" />
          </div>
        </div>
        <div className="md:col-span-3">
          <Label className="text-xs">Cliente</Label>
          <Select value={filterCliente || "todos"} onValueChange={(v) => setFilterCliente(v === "todos" ? "" : v)}>
            <SelectTrigger data-testid="invoice-filter-cliente"><SelectValue placeholder="Todos los clientes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos los clientes</SelectItem>
              {clientes.map(c => <SelectItem key={c.id} value={c.id}>{c.razon_social}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Estado SRI</Label>
          <Select value={filterEstado} onValueChange={setFilterEstado}>
            <SelectTrigger data-testid="invoice-filter-estado"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="borrador">Borrador</SelectItem>
              <SelectItem value="firmado">Firmado</SelectItem>
              <SelectItem value="enviado">Enviado</SelectItem>
              <SelectItem value="autorizado">Autorizado</SelectItem>
              <SelectItem value="rechazado">Rechazado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1"><Label className="text-xs">Desde</Label><Input type="date" value={filterFechaDesde} onChange={(e) => setFilterFechaDesde(e.target.value)} data-testid="invoice-filter-desde" /></div>
        <div className="md:col-span-1"><Label className="text-xs">Hasta</Label><Input type="date" value={filterFechaHasta} onChange={(e) => setFilterFechaHasta(e.target.value)} data-testid="invoice-filter-hasta" /></div>
        <div className="md:col-span-1">
          <Button variant="outline" size="sm" onClick={() => { setSearchQ(""); setFilterCliente(""); setFilterEstado("todos"); setFilterFechaDesde(""); setFilterFechaHasta(""); }} className="w-full" data-testid="invoice-filter-clear">Limpiar</Button>
        </div>
        <div className="md:col-span-12 text-[10px] text-[#5c7368]">
          Mostrando <strong>{filteredItems.length}</strong> de {items.length} facturas
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-x-auto">
        <table className="erp-table">
          <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th className="font-mono text-[10px]">Clave acceso</th><th className="text-right">Total</th><th>Estado SRI</th><th className="w-32 text-right">Acciones</th></tr></thead>
          <tbody>
            {pagedFacturas.paged.map(it => (
              <tr key={it.id} data-invoice-row={it.id} className="transition-all">
                <td className="font-mono text-xs font-semibold">{it.numero}</td>
                <td>{it.fecha}</td>
                <td className="font-medium">
                  <div>{it.cliente_nombre}</div>
                  {it.cliente_nombre_comercial && it.cliente_nombre_comercial !== it.cliente_nombre && (
                    <div className="text-[10px] text-[#5c7368] font-normal italic">{it.cliente_nombre_comercial}</div>
                  )}
                </td>
                <td className="font-mono text-[10px] text-[#5c7368]">{it.clave_acceso?.slice(0,12)}...{it.clave_acceso?.slice(-6)}</td>
                <td className="text-right font-semibold tabular-nums">${it.total?.toFixed(2)}</td>
                <td><Badge className={`${ESTADO_STYLE[it.estado_sri] || ""} border capitalize`}>{it.estado_sri}</Badge></td>
                <td>
                  <div className="flex gap-1 justify-end items-center">
                    {/* Acción primaria por estado */}
                    {it.estado_sri !== "autorizado" ? (
                      <>
                        {(it.estado_sri === "borrador" || it.estado_sri === "firmado" || it.estado_sri === "enviado" || it.estado_sri === "rechazado") && (
                          <Button
                            size="sm"
                            className="bg-[#205c45] hover:bg-[#1a4a37] h-8"
                            onClick={() => procesarSRI(it)}
                            disabled={!!accionInFlight[it.id]}
                            data-testid={`procesar-sri-${it.id}`}
                            title="Firmar → Enviar al SRI → Autorizar (un solo paso)"
                          >
                            {accionInFlight[it.id] ? (
                              <><Loader2 size={13} className="mr-1 animate-spin" />Procesando…</>
                            ) : (
                              <><CheckCircle2 size={13} className="mr-1" />
                                {it.estado_sri === "enviado" ? "Reintentar" : "Firmar+SRI"}
                              </>
                            )}
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-8" onClick={() => previewRIDE(it)} title="Vista previa del RIDE" data-testid={`preview-${it.id}`}>
                          <Eye size={13} />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" className="text-[#205c45] h-8" onClick={() => descargarRIDE(it)} title="Descargar RIDE PDF" data-testid={`ride-${it.id}`}>
                          <FileText size={13} className="mr-1" />RIDE
                        </Button>
                        <Button size="sm" variant="outline" className="h-8" onClick={() => shareRide(it)} title="Compartir por WhatsApp / Email" data-testid={`share-${it.id}`}>
                          <Share2 size={13} />
                        </Button>
                      </>
                    )}

                    {/* Dropdown con todas las acciones adicionales */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" className="h-8 px-2" data-testid={`fact-menu-${it.id}`} title="Más acciones">
                          <MoreHorizontal size={14} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#5c7368]">Descargas</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => descargarXML(it)} data-testid={`menu-xml-${it.id}`}>
                          <Download size={13} className="mr-2" />
                          {it.estado_sri === "autorizado" ? "XML autorizado (SRI)" : "XML sin firmar"}
                        </DropdownMenuItem>
                        {it.xml_firmado_existe !== false && (it.estado_sri === "firmado" || it.estado_sri === "enviado" || it.estado_sri === "autorizado") && (
                          <DropdownMenuItem onClick={() => descargarXMLFirmado(it)} data-testid={`menu-xml-firm-${it.id}`}>
                            <Download size={13} className="mr-2" />XML firmado
                          </DropdownMenuItem>
                        )}
                        {it.estado_sri === "autorizado" && (
                          <>
                            <DropdownMenuItem onClick={() => enviarEmail(it)} data-testid={`menu-email-${it.id}`}>
                              <Mail size={13} className="mr-2" />Enviar por email
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#5c7368]">Documentos derivados</DropdownMenuLabel>
                            <DropdownMenuItem onClick={() => iniciarGuia(it)} data-testid={`menu-guia-${it.id}`}>
                              <Truck size={13} className="mr-2" />Guía de remisión
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => iniciarCarta(it, "canje")} data-testid={`menu-cc-${it.id}`}>
                              <FileBadge2 size={13} className="mr-2" />Carta de canje
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => iniciarCarta(it, "garantia")} data-testid={`menu-gt-${it.id}`}>
                              <FileBadge2 size={13} className="mr-2" />Garantía técnica
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => iniciarDevolucion(it)} className="text-[#b08400] focus:text-[#b08400]" data-testid={`menu-dev-${it.id}`}>
                              <RotateCcw size={13} className="mr-2" />Devolución (NC)
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[#5c7368]">Edición</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => abrirSoftEdit(it)} data-testid={`menu-locales-${it.id}`}>
                          <FileEdit size={13} className="mr-2" />Editar campos locales
                        </DropdownMenuItem>
                        {["borrador", "firmado", "rechazado"].includes(it.estado_sri) && (
                          <DropdownMenuItem onClick={() => editarFactura(it)} data-testid={`menu-edit-${it.id}`}>
                            <Edit2 size={13} className="mr-2" />Editar factura
                          </DropdownMenuItem>
                        )}
                        {["borrador", "firmado", "rechazado", "enviado"].includes(it.estado_sri) && (
                          <DropdownMenuItem onClick={() => renumerar(it)} className="text-[#a35400] focus:text-[#a35400]" data-testid={`menu-renumerar-${it.id}`}>
                            <Hash size={13} className="mr-2" />Renumerar (secuencial registrado)
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => navigate(`/facturacion/nueva?duplicar=${it.id}`)} data-testid={`menu-dup-${it.id}`}>
                          <Copy size={13} className="mr-2" />Duplicar
                        </DropdownMenuItem>
                        {["admin","gerencia","contabilidad","contador"].includes((user?.role||"").toLowerCase()) && (
                          <DropdownMenuItem onClick={() => setReasignarFc(it)} data-testid={`menu-cliente-${it.id}`}>
                            <UserCog size={13} className="mr-2" />Reasignar cliente
                          </DropdownMenuItem>
                        )}
                        {(it.edit_history || []).length > 0 && (
                          <DropdownMenuItem onClick={() => setHistoryOpen(it)} data-testid={`menu-hist-${it.id}`}>
                            <History size={13} className="mr-2" />Historial
                          </DropdownMenuItem>
                        )}
                        {it.estado_sri !== "anulado" && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setAnularSriFc(it)}
                              className="text-[#b53a25] focus:text-[#b53a25]"
                              data-testid={`menu-anular-sri-${it.id}`}
                            >
                              <Ban size={13} className="mr-2" />Marcar como anulada en SRI
                            </DropdownMenuItem>
                          </>
                        )}
                        {(it.estado_sri === "borrador" || it.estado_sri === "rechazado") && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => eliminarDoc(it)} className="text-[#b53a25] focus:text-[#b53a25]" data-testid={`menu-del-${it.id}`}>
                              <Trash2 size={13} className="mr-2" />Eliminar
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]"><FileText className="inline mb-2" size={28} /><br />Sin facturas emitidas aún</td></tr>}
            {items.length > 0 && filteredItems.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]">Sin resultados para los filtros aplicados</td></tr>}
          </tbody>
        </table>
        <PaginationBar
          page={pagedFacturas.page}
          total={pagedFacturas.total}
          pageSize={pagedFacturas.pageSize}
          onPrev={pagedFacturas.prev}
          onNext={pagedFacturas.next}
          label="facturas"
        />
      </div>

      {/* Selector de línea para generar carta desde factura con múltiples productos */}

      <ReasignarClienteDialog
        factura={reasignarFc}
        clientes={clientes}
        onClose={() => setReasignarFc(null)}
        onSaved={() => { setReasignarFc(null); load(); }}
      />

      <MarcarAnuladaSriDialog
        factura={anularSriFc}
        onClose={() => setAnularSriFc(null)}
        onSaved={() => { setAnularSriFc(null); load(); }}
      />

      <Dialog open={!!cartaPicker} onOpenChange={(o) => !o && setCartaPicker(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>
              Elegir producto para carta {cartaPicker?.tipo === "canje" ? "de canje" : "de garantía técnica"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#4a5c53] mb-3">
            La factura <strong>{cartaPicker?.factura?.numero}</strong> tiene varios productos. Seleccione el que desea certificar.
            Puede generar una carta adicional luego si necesita otro producto.
          </p>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {cartaPicker?.factura?.lineas?.map((l, i) => (
              <button
                key={i}
                onClick={() => generarCarta(cartaPicker.factura, cartaPicker.tipo, i)}
                className="w-full text-left rounded-md border border-[#d1dbd6] bg-white hover:bg-[#e9f2ee] hover:border-[#205c45] p-3 transition-colors"
                data-testid={`pick-line-${i}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-[#205c45] text-white text-xs font-bold grid place-items-center shrink-0">{i + 1}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-[#15241d]">{l.producto_nombre || l.descripcion || "—"}</div>
                    <div className="text-xs text-[#5c7368] mt-0.5 flex gap-4">
                      <span>Cant: <strong>{l.cantidad}</strong></span>
                      {l.codigo_interno && <span>Código: <span className="font-mono">{l.codigo_interno}</span></span>}
                      <span className="tabular-nums">${(l.subtotal || 0).toFixed(2)}</span>
                    </div>
                  </div>
                  <FileBadge2 className="text-[#205c45]" size={18} />
                </div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCartaPicker(null)}>Cancelar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* ITER148: Dialog soft-edit de campos locales */}
      <Dialog open={!!softEditDialog} onOpenChange={(v) => { if (!v) setSoftEditDialog(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="soft-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              <FileEdit className="inline mr-2 mb-1" size={18} />
              Editar campos locales · Factura {softEditDialog?.factura?.numero_completo || softEditDialog?.factura?.numero}
            </DialogTitle>
          </DialogHeader>
          {softEditDialog && (
            <div className="space-y-3">
              <div className="bg-[#fff4e6] border border-[#f5c684] rounded p-3 text-[12px] text-[#a55700]">
                <strong>Importante:</strong> Estos cambios afectan SOLO el RIDE/PDF local y la CxC asociada.
                El XML enviado al SRI <u>no se modifica</u> (la factura ya fue autorizada o es una migración histórica).
                Estado actual SRI: <Badge variant="outline" className="ml-1 capitalize">{softEditDialog.factura.estado_sri}</Badge>
              </div>

              <div>
                <Label className="text-xs">Observaciones (visibles en el RIDE/PDF local)</Label>
                <Textarea
                  rows={2}
                  value={softEditDialog.form.observaciones}
                  onChange={(e) => setSoftEditDialog(s => ({ ...s, form: { ...s.form, observaciones: e.target.value } }))}
                  data-testid="soft-edit-observaciones"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Días de crédito</Label>
                  <Input
                    type="number" min="0"
                    value={softEditDialog.form.dias_credito}
                    onChange={(e) => setSoftEditDialog(s => ({
                      ...s, form: { ...s.form, dias_credito: e.target.value, fecha_vencimiento: "" }
                    }))}
                    data-testid="soft-edit-dias-credito"
                  />
                  <p className="text-[10px] text-[#5c7368] mt-1">Recalcula fecha de vencimiento desde la fecha de la factura.</p>
                </div>
                <div>
                  <Label className="text-xs">Fecha de vencimiento (alternativa)</Label>
                  <Input
                    type="date"
                    value={softEditDialog.form.fecha_vencimiento}
                    onChange={(e) => setSoftEditDialog(s => ({
                      ...s, form: { ...s.form, fecha_vencimiento: e.target.value, dias_credito: "" }
                    }))}
                    data-testid="soft-edit-fecha-vencimiento"
                  />
                  <p className="text-[10px] text-[#5c7368] mt-1">Si la pones, los días de crédito se calculan automáticamente.</p>
                </div>
              </div>

              <div>
                <Label className="text-xs">Dirección de entrega (opcional)</Label>
                <Input
                  value={softEditDialog.form.direccion_entrega}
                  onChange={(e) => setSoftEditDialog(s => ({ ...s, form: { ...s.form, direccion_entrega: e.target.value } }))}
                  data-testid="soft-edit-direccion"
                />
              </div>

              <div>
                <Label className="text-xs">Comentarios internos (no aparecen en el PDF del cliente)</Label>
                <Textarea
                  rows={2}
                  value={softEditDialog.form.comentarios_internos}
                  onChange={(e) => setSoftEditDialog(s => ({ ...s, form: { ...s.form, comentarios_internos: e.target.value } }))}
                  data-testid="soft-edit-comentarios"
                />
              </div>

              <div>
                <Label className="text-xs">Campos adicionales (formato: <code>nombre|valor</code> por línea)</Label>
                <Textarea
                  rows={4}
                  value={softEditDialog.form.info_adicional_text}
                  onChange={(e) => setSoftEditDialog(s => ({ ...s, form: { ...s.form, info_adicional_text: e.target.value } }))}
                  placeholder={"Email|cliente@ejemplo.com\nTelefono|0999999999\nVendedor|Juan Pérez"}
                  data-testid="soft-edit-info-adicional"
                />
                <p className="text-[10px] text-[#5c7368] mt-1">
                  Reemplaza la lista completa. Líneas vacías y campos sin valor se ignoran.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSoftEditDialog(null)}>Cancelar</Button>
            <Button onClick={guardarSoftEdit} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="soft-edit-save-btn">
              Guardar campos locales
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!historyOpen} onOpenChange={(v) => { if (!v) setHistoryOpen(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle><History className="inline mr-2" size={18} />Historial · {historyOpen?.numero}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {(historyOpen?.edit_history || []).length === 0 && <p className="text-sm text-[#5c7368]">No hay ediciones registradas.</p>}
            {(historyOpen?.edit_history || []).map((h, idx) => (
              <div key={idx} className="border-l-2 border-[#205c45] pl-3 py-2 bg-[#f7f9f8] rounded">
                <div className="text-xs text-[#5c7368]">{(h.fecha || "").slice(0, 16).replace("T", " ")} · <b>{h.user_nombre}</b></div>
                <ul className="text-sm mt-1 space-y-0.5 list-disc pl-5">
                  {(h.cambios || []).map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Sheet: Generar Guía de Remisión desde factura */}
      <Sheet open={!!guiaSheet} onOpenChange={(v) => { if (!v) setGuiaSheet(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-[#d1dbd6]">
            <SheetTitle className="flex items-center gap-2"><Truck size={18} className="text-[#205c45]" /> Guía de Remisión · Factura {guiaSheet?.factura?.numero}</SheetTitle>
            <p className="text-xs text-[#5c7368]">Confirme los datos del transporte y la cantidad de cartones antes de generar la guía.</p>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {guiaSheet && (<>
              {/* Cartones - lo único que cambia por factura */}
              <div className="rounded-md border-2 border-[#205c45] bg-[#f0f7f3] p-3">
                <Label className="text-[#205c45] font-semibold text-base">¿Cuántos cartones se envían? *</Label>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={guiaSheet.cartones}
                  onChange={(e) => setGuiaSheet({ ...guiaSheet, cartones: e.target.value })}
                  className="mt-2 text-2xl h-12 font-bold text-center"
                  data-testid="guia-cartones-input"
                  autoFocus
                />
                <p className="text-[11px] text-[#5c7368] mt-1">Este dato aparecerá en el motivo de traslado de la guía.</p>
              </div>

              {/* Transportista */}
              <div>
                <h3 className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">Transportista (defaults de Empresa)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2 space-y-1.5">
                    <Label>Razón social</Label>
                    <Input value={guiaSheet.transportista.razon_social_transportista} onChange={(e) => setGuiaSheet({ ...guiaSheet, transportista: { ...guiaSheet.transportista, razon_social_transportista: e.target.value } })} data-testid="guia-transp-razon" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tipo identificación</Label>
                    <Select value={guiaSheet.transportista.tipo_identificacion_transportista} onValueChange={(v) => setGuiaSheet({ ...guiaSheet, transportista: { ...guiaSheet.transportista, tipo_identificacion_transportista: v } })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ruc">RUC</SelectItem>
                        <SelectItem value="cedula">Cédula</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>RUC / Cédula</Label>
                    <Input value={guiaSheet.transportista.ruc_transportista} onChange={(e) => setGuiaSheet({ ...guiaSheet, transportista: { ...guiaSheet.transportista, ruc_transportista: e.target.value } })} className="font-mono" data-testid="guia-transp-ruc" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Placa</Label>
                    <Input value={guiaSheet.transportista.placa} onChange={(e) => setGuiaSheet({ ...guiaSheet, transportista: { ...guiaSheet.transportista, placa: e.target.value.toUpperCase() } })} className="font-mono" data-testid="guia-transp-placa" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Fecha emisión</Label>
                    <Input type="date" value={guiaSheet.fechas.fecha} onChange={(e) => setGuiaSheet({ ...guiaSheet, fechas: { ...guiaSheet.fechas, fecha: e.target.value } })} />
                  </div>
                  <div className="md:col-span-2 space-y-1.5">
                    <Label>Dirección de partida</Label>
                    <Input value={guiaSheet.transportista.dir_partida} onChange={(e) => setGuiaSheet({ ...guiaSheet, transportista: { ...guiaSheet.transportista, dir_partida: e.target.value } })} data-testid="guia-transp-dir" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Inicio transporte</Label>
                    <Input type="date" value={guiaSheet.fechas.fecha_ini_transporte} onChange={(e) => setGuiaSheet({ ...guiaSheet, fechas: { ...guiaSheet.fechas, fecha_ini_transporte: e.target.value } })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Fin transporte</Label>
                    <Input type="date" value={guiaSheet.fechas.fecha_fin_transporte} onChange={(e) => setGuiaSheet({ ...guiaSheet, fechas: { ...guiaSheet.fechas, fecha_fin_transporte: e.target.value } })} />
                  </div>
                </div>
                <p className="text-[11px] text-[#5c7368] mt-2">
                  💡 Estos campos se llenan desde <b>Empresa y logo</b>. Edítelos solo si esta guía tiene un transportista o ruta distinta.
                </p>
              </div>

              {/* Destinatario */}
              <div>
                <h3 className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">Destinatario (cliente de la factura)</h3>
                <div className="rounded-md border border-[#d1dbd6] bg-[#f7f9f8] p-3 text-sm space-y-1">
                  <div><b>{guiaSheet.destinatario.razon_social}</b></div>
                  <div className="font-mono text-xs text-[#5c7368]">{guiaSheet.destinatario.identificacion}</div>
                  <div className="text-xs text-[#5c7368]">{guiaSheet.destinatario.direccion || "Sin dirección"}</div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="space-y-1.5">
                    <Label>Motivo traslado</Label>
                    <Input value={guiaSheet.destinatario.motivo_traslado} onChange={(e) => setGuiaSheet({ ...guiaSheet, destinatario: { ...guiaSheet.destinatario, motivo_traslado: e.target.value } })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Ruta (opcional)</Label>
                    <Input value={guiaSheet.destinatario.ruta || ""} onChange={(e) => setGuiaSheet({ ...guiaSheet, destinatario: { ...guiaSheet.destinatario, ruta: e.target.value } })} placeholder="Ej: Guayaquil → Quito" />
                  </div>
                </div>
              </div>

              {/* Detalle de items */}
              <div>
                <h3 className="text-xs uppercase tracking-wider font-semibold text-[#5c7368] mb-2">
                  Detalle ({guiaSheet.destinatario.detalles.length} línea{guiaSheet.destinatario.detalles.length !== 1 ? "s" : ""}) · auto-cargado de la factura
                </h3>
                <div className="rounded-md border border-[#d1dbd6] max-h-48 overflow-y-auto">
                  <table className="erp-table text-xs">
                    <thead><tr><th>Código</th><th>Descripción</th><th className="text-right w-20">Cant.</th></tr></thead>
                    <tbody>
                      {guiaSheet.destinatario.detalles.map((d, i) => (
                        <tr key={i}><td className="font-mono">{d.codigo_interno}</td><td>{d.descripcion}</td><td className="text-right tabular-nums">{d.cantidad}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>)}
          </div>
          <SheetFooter className="border-t border-[#d1dbd6] px-6 py-4 gap-2">
            <Button variant="outline" onClick={() => setGuiaSheet(null)} disabled={guiaSaving}>Cancelar</Button>
            <Button onClick={crearGuia} disabled={guiaSaving} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="guia-confirmar-btn">
              {guiaSaving ? <><Loader2 size={14} className="mr-2 animate-spin" />Creando…</> : <><Truck size={14} className="mr-2" />Crear guía</>}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Dialog Devolución → Nota de Crédito */}
      <Dialog open={!!devolucionDialog} onOpenChange={(v) => { if (!v) setDevolucionDialog(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Devolución de factura {devolucionDialog?.factura?.numero}
            </DialogTitle>
          </DialogHeader>
          {devolucionDialog && (
            <div className="space-y-3">
              <div className="text-sm text-[#5c7368]">
                Cliente: <strong className="text-[#15241d]">{devolucionDialog.factura.cliente_nombre}</strong>
                {" · "}Total facturado: <strong className="text-[#15241d]">${devolucionDialog.factura.total?.toFixed(2)}</strong>
              </div>
              <div>
                <Label>Motivo de la devolución *</Label>
                <Input
                  value={devolucionDialog.motivo}
                  onChange={(e) => setDevolucionDialog({ ...devolucionDialog, motivo: e.target.value })}
                  placeholder="Ej: Producto dañado, vencido próximo a vencer, error de pedido…"
                  data-testid="devolucion-motivo"
                />
              </div>
              <div className="border border-[#d1dbd6] rounded">
                <table className="w-full text-sm">
                  <thead className="bg-[#e9f2ee] text-[#15241d]">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className="p-2 text-left">Producto</th>
                      <th className="p-2 text-right">Facturado</th>
                      <th className="p-2 text-right">A devolver</th>
                      <th className="p-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devolucionDialog.lineas.map((ln, i) => (
                      <tr key={i} className="border-t border-[#d1dbd6]">
                        <td className="p-2">
                          <input
                            type="checkbox"
                            checked={ln.sel}
                            onChange={(e) => {
                              const upd = [...devolucionDialog.lineas];
                              upd[i] = { ...ln, sel: e.target.checked };
                              setDevolucionDialog({ ...devolucionDialog, lineas: upd });
                            }}
                          />
                        </td>
                        <td className="p-2">{ln.producto_nombre}</td>
                        <td className="p-2 text-right tabular-nums">{ln.cant_max}</td>
                        <td className="p-2 text-right">
                          <input
                            type="number"
                            min="0"
                            max={ln.cant_max}
                            step="0.01"
                            value={ln.cant}
                            disabled={!ln.sel}
                            onChange={(e) => {
                              const upd = [...devolucionDialog.lineas];
                              upd[i] = { ...ln, cant: Math.min(Number(e.target.value), ln.cant_max) };
                              setDevolucionDialog({ ...devolucionDialog, lineas: upd });
                            }}
                            className="w-20 h-7 border border-[#d1dbd6] rounded text-right px-1"
                          />
                        </td>
                        <td className="p-2 text-right tabular-nums">
                          ${(ln.precio_unitario * (ln.sel ? Number(ln.cant) || 0 : 0)).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="reponer-stock"
                  type="checkbox"
                  checked={!!devolucionDialog.reponer}
                  onChange={(e) => setDevolucionDialog({ ...devolucionDialog, reponer: e.target.checked })}
                />
                <label htmlFor="reponer-stock" className="text-sm">
                  Reponer stock al lote original (desmarcar si la mercadería viene dañada/vencida)
                </label>
              </div>
              <div className="text-xs text-[#5c7368] bg-[#fef7e0] border border-[#e5cf99] rounded p-2">
                ℹ️ Se generará una <strong>Nota de Crédito electrónica</strong> en estado borrador.
                Luego ve a <strong>Notas de Crédito</strong> para firmarla y enviarla al SRI.
                La CxC del cliente se reduce automáticamente.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDevolucionDialog(null)}>Cancelar</Button>
            <Button
              onClick={confirmarDevolucion}
              className="bg-[#b08400] hover:bg-[#8f6c00]"
              data-testid="confirmar-devolucion-btn"
            >
              <RotateCcw size={14} className="mr-2" /> Generar Nota de Crédito
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ITER227: selección de correos del cliente */}
      <Dialog open={!!emailDialog} onOpenChange={(v) => !v && setEmailDialog(null)}>
        <DialogContent className="max-w-md" data-testid="email-destinos-dialog">
          <DialogHeader>
            <DialogTitle>Enviar factura {emailDialog?.factura?.numero}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#4a5c53]">
            Se enviará a los correos marcados del cliente (RIDE en PDF + XML autorizado).
          </p>
          <div className="space-y-2">
            {(emailDialog?.correos || []).map((c, i) => (
              <label key={i} className="flex items-center gap-2 rounded border border-[#d1dbd6] p-2 text-sm cursor-pointer">
                <input type="checkbox" className="h-4 w-4 accent-[#205c45]" checked={c.sel}
                  onChange={(e) => setEmailDialog(d => ({
                    ...d, correos: d.correos.map((x, j) => (j === i ? { ...x, sel: e.target.checked } : x)),
                  }))}
                  data-testid={`email-destino-${i}`} />
                <span className="flex-1">
                  {c.email}
                  <span className="text-[11px] text-[#5c7368] ml-1">
                    ({c.etiqueta}{c.principal ? " · principal" : ""})
                  </span>
                </span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(null)}>Cancelar</Button>
            <Button className="bg-[#205c45]" data-testid="email-enviar-btn"
              onClick={() => {
                const emails = (emailDialog.correos || []).filter(c => c.sel).map(c => c.email);
                if (emails.length === 0) return toast.error("Seleccione al menos un correo");
                confirmarEnvioEmail(emailDialog.factura, emails);
              }}>
              <Mail size={14} className="mr-2" />Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );

}

function ReasignarClienteDialog({ factura, clientes, onClose, onSaved }) {
  const [nuevoId, setNuevoId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const [filtro, setFiltro] = useState("");

  useEffect(() => { if (factura) { setNuevoId(""); setMotivo(""); setFiltro(""); } }, [factura]);

  if (!factura) return null;
  const filtered = (clientes || []).filter(c => {
    if (!filtro.trim()) return true;
    const q = filtro.trim().toLowerCase();
    return (c.razon_social || "").toLowerCase().includes(q) ||
           (c.numero_identificacion || "").toLowerCase().includes(q);
  }).slice(0, 50);

  const nuevoCli = (clientes || []).find(c => c.id === nuevoId);

  const guardar = async () => {
    if (!nuevoId) { toast.error("Selecciona el cliente correcto"); return; }
    if (nuevoId === factura.cliente_id) { toast.error("Es el mismo cliente, no hay cambio"); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/invoicing/facturas/${factura.id}/cliente`, {
        cliente_id: nuevoId,
        motivo: motivo.trim() || null,
      });
      toast.success(`Cliente reasignado: "${data.cliente_anterior}" → "${data.cliente_nuevo}"`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!factura} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="reasignar-cliente-dialog">
        <DialogHeader>
          <DialogTitle>Reasignar cliente · Factura {factura.numero}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-[#fef7ec] border border-[#f5c477] rounded p-3 text-[12px] text-[#7a4000]">
            <strong>⚠ Solo corrige la referencia interna del ERP.</strong> El comprobante autorizado por SRI (XML, clave de acceso, autorización) NO se modifica. Útil cuando una migración asignó esta factura al cliente equivocado.
          </div>

          <div className="bg-[#f5f8f6] rounded p-3 text-sm">
            <div className="text-[10px] uppercase text-[#5c7368]">Cliente actual</div>
            <div className="font-semibold">{factura.cliente_nombre || "—"}</div>
            <div className="text-[11px] text-[#5c7368] font-mono">{factura.cliente_identificacion || "—"}</div>
          </div>

          <div>
            <Label className="text-xs">Buscar cliente nuevo (RUC, cédula o razón social)</Label>
            <Input value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Ej. 1791123456001 o LACFARMA..." className="mt-1" data-testid="reasignar-search" />
          </div>

          <div className="max-h-64 overflow-y-auto border rounded">
            {filtered.length === 0 && <div className="p-3 text-center text-[11px] text-[#8ba397]">{filtro ? "Sin coincidencias" : "Escribe para buscar"}</div>}
            {filtered.map(c => (
              <label
                key={c.id}
                className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b border-[#eee] hover:bg-[#f5f8f6] ${nuevoId === c.id ? "bg-[#e9f2ee]" : ""}`}
                data-testid={`reasignar-opt-${c.id}`}
              >
                <input type="radio" name="cliente_nuevo" checked={nuevoId === c.id} onChange={() => setNuevoId(c.id)} />
                <div className="flex-1">
                  <div className="font-medium text-sm">{c.razon_social}</div>
                  <div className="text-[10px] font-mono text-[#5c7368]">{c.tipo_identificacion?.toUpperCase()} {c.numero_identificacion}</div>
                </div>
                {c.id === factura.cliente_id && <Badge variant="outline" className="text-[10px]">Actual</Badge>}
              </label>
            ))}
          </div>

          <div>
            <Label className="text-xs">Motivo (opcional, queda en auditoría)</Label>
            <Textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2} placeholder="Ej. Corrección de migración: factura emitida a LACFARMA por error" />
          </div>

          {nuevoCli && nuevoId !== factura.cliente_id && (
            <div className="bg-[#e9f2ee] border border-[#9ec4b1] rounded p-3 text-sm">
              <div className="text-[10px] uppercase text-[#205c45]">Cliente nuevo confirmado</div>
              <div className="font-bold text-[#15241d]">{nuevoCli.razon_social}</div>
              <div className="text-[11px] font-mono text-[#205c45]">{nuevoCli.numero_identificacion}</div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            onClick={guardar}
            disabled={saving || !nuevoId || nuevoId === factura.cliente_id}
            className="bg-[#a55700] hover:bg-[#7a4000] text-white"
            data-testid="reasignar-guardar"
          >
            {saving ? "Guardando..." : "Reasignar cliente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

